import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Rocket, TrendingUp, Wallet, Lock } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/common/Reveal";
import { Overline } from "@/components/common/SectionHeading";
import { GlowBackground, ParallaxWrap } from "@/components/common/GlowBackground";
import { FButton } from "@/components/common/FButton";
import { TrustStrip } from "@/components/common/TrustStrip";
import { EditorialMarquee } from "@/components/common/EditorialMarquee";
import { CTASection } from "@/components/common/CTASection";
import { WhyCreators } from "@/components/common/WhyCreators";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PILLAR_CARDS, MANIFESTO, JOURNEY, BRAND, IMAGES } from "@/data/content";

const accentText = {
  ember: "text-ff-ember",
  teal: "text-ff-teal",
  amber: "text-ff-amber",
  ivory: "text-ff-ivory",
};
const accentBorder = {
  ember: "hover:border-ff-ember/40",
  teal: "hover:border-ff-teal/40",
  amber: "hover:border-ff-amber/40",
  ivory: "hover:border-ff-ivory/30",
};
const pillarIcons = { launch: Rocket, grow: TrendingUp, cashout: Wallet, control: Lock };

const Hero = () => (
  <section className="relative min-h-[92vh] overflow-hidden px-5 pb-24 pt-28 md:px-8 md:pb-16 md:pt-36" data-testid="home-hero">
    <GlowBackground />
    <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.15fr_0.85fr]">
      {/* copy */}
      <div>
        <Reveal>
          <StatusBadge tone="ember">Creator infrastructure · Caribbean</StatusBadge>
        </Reveal>

        <h1 className="mt-6 font-display text-[2.6rem] font-black leading-[0.96] tracking-tight text-ff-ivory sm:text-6xl lg:text-[4.4rem]">
          <MaskedLines
            lines={["Turn your audience", "into income —"]}
            start={0.15}
          />
          <span className="block overflow-hidden">
            <motion.span
              className="block text-ff-ember"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.39, ease: [0.22, 1, 0.36, 1] }}
            >
              without giving up control.
            </motion.span>
          </span>
        </h1>

        <Reveal delay={0.55} className="mt-7 max-w-xl">
          <p className="text-base leading-relaxed text-ff-grey md:text-lg">
            FanFuel helps Caribbean creators launch paid platforms, grow with AI-assisted
            coaching, and manage cashouts through a clear, approval-controlled process.
          </p>
        </Reveal>

        <Reveal delay={0.7} className="mt-9 flex flex-col gap-3 sm:flex-row">
          <FButton to="/apply" data-testid="hero-apply-btn">
            Apply to FanFuel <ArrowRight className="h-4 w-4" />
          </FButton>
          <FButton variant="secondary" to="/how-it-works" data-testid="hero-how-btn">
            See how FanFuel works
          </FButton>
        </Reveal>

        <Reveal delay={0.85}>
          <TrustStrip className="mt-10" />
        </Reveal>
      </div>

      {/* visual */}
      <ParallaxWrap offset={40} className="relative hidden lg:block">
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          {/* clipped spotlight portrait */}
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10">
            <img
              src={IMAGES.heroCreator}
              alt="A confident creator"
              className="h-[520px] w-full object-cover"
              style={{ maskImage: "linear-gradient(to bottom, black 82%, transparent)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ff-midnight/70 via-transparent to-transparent" />
          </div>

          {/* floating dashboard card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="absolute -bottom-6 -left-8 w-64 rounded-2xl border border-white/10 bg-ff-slate/90 p-5 backdrop-blur-xl"
          >
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ff-grey/70">
              Example status card
            </p>
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs uppercase tracking-widest text-ff-grey">Cashout</span>
              <StatusBadge tone="neutral">Cashout review</StatusBadge>
            </div>
            <p className="mt-3 font-display text-2xl font-bold text-ff-ivory">Ref · CR-8F2A</p>
            <div className="mt-3 flex items-center gap-2 text-xs text-ff-grey">
              <ShieldCheck className="h-3.5 w-3.5 text-ff-teal" />
              Reviewed by a human
            </div>
          </motion.div>
        </motion.div>
      </ParallaxWrap>
    </div>
  </section>
);

const Pillars = () => (
  <section className="px-5 py-20 md:px-8 md:py-28" data-testid="home-pillars">
    <div className="mx-auto max-w-7xl">
      <Reveal className="max-w-2xl">
        <Overline>What FanFuel gives you</Overline>
        <h2 className="mt-4 font-display text-4xl font-bold leading-tight tracking-tight text-ff-ivory md:text-5xl">
          Launch. Grow. Cash out. Control.
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {PILLAR_CARDS.map((card, i) => {
          const Icon = pillarIcons[card.key];
          return (
            <Reveal key={card.key} delay={i * 0.08}>
              <div
                data-testid={`pillar-card-${card.key}`}
                className={`group h-full rounded-2xl border border-white/5 bg-ff-slate/60 p-7 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 ${accentBorder[card.accent]}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`font-mono text-sm ${accentText[card.accent]}`}>{card.tag}</span>
                  <Icon className={`h-6 w-6 ${accentText[card.accent]}`} />
                </div>
                <h3 className="mt-8 font-display text-2xl font-semibold text-ff-ivory">{card.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ff-grey">{card.body}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Manifesto = () => (
  <section className="relative px-5 py-20 md:px-8 md:py-28" data-testid="home-manifesto">
    <div className="mx-auto max-w-7xl">
      <Reveal className="max-w-2xl">
        <Overline>The FanFuel promise</Overline>
        <h2 className="mt-4 font-display text-4xl font-bold leading-tight tracking-tight text-ff-ivory md:text-5xl text-balance">
          {BRAND.trust}
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/5 md:grid-cols-2">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.n} delay={i * 0.06} className="bg-ff-midnight">
            <div className="h-full p-8 md:p-10">
              <span className="font-mono text-sm text-ff-ember">{m.n}</span>
              <h3 className="mt-6 font-display text-2xl font-semibold text-ff-ivory md:text-3xl">{m.title}</h3>
              <p className="mt-4 max-w-md text-base leading-relaxed text-ff-grey">{m.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const JourneyPreview = () => (
  <section className="px-5 py-20 md:px-8 md:py-28" data-testid="home-journey">
    <div className="mx-auto max-w-7xl">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <Reveal className="max-w-2xl">
          <Overline>The journey</Overline>
          <h2 className="mt-4 font-display text-4xl font-bold leading-tight tracking-tight text-ff-ivory md:text-5xl">
            From application to approved cashout.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <FButton variant="secondary" to="/how-it-works" data-testid="journey-more-btn">
            See the full process <ArrowRight className="h-4 w-4" />
          </FButton>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {JOURNEY.slice(0, 4).map((s, i) => (
          <Reveal key={s.step} delay={i * 0.08}>
            <div className="h-full rounded-2xl border border-white/5 bg-ff-slate/50 p-6">
              <span className="font-mono text-xs text-ff-ember">{s.step}</span>
              <h3 className="mt-4 font-display text-lg font-semibold text-ff-ivory">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ff-grey">{s.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      <Pillars />
      <Manifesto />
      <JourneyPreview />
      <WhyCreators />
      <CTASection />
    </div>
  );
}
